import React, { useEffect, useState } from "react";
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import PropTypes from "prop-types";
import stringUtils from "../../../_services/string-utils";
import listUtils from "../../../_services/list-utils";
import styled from "styled-components";
import { MdClose } from "react-icons/md";
import { PathwayTooltip } from "../PathwayTooltip";

const UncontrolledTabNavigation = ({
    defaultTab,
    currentTab,
    tabList,
    children,
    dataCy,
    removeTab,
    addNewTab,
    availableTabs,
}) => {
    const [activeTab, setActiveTab] = useState(defaultTab);

    useEffect(() => {
        if (currentTab) {
            setActiveTab(currentTab);
        }
    }, [currentTab, setActiveTab]);

    const childArray = React.Children.toArray(children);

    return (
        <>
            {tabList && (
                <>
                    <Nav tabs style={{ width: "100 %", border: "none" }} data-cy={dataCy || undefined}>
                        {tabList.map((item, index) => {
                            return (
                                <CustomNavItem
                                    key={index}
                                    active={activeTab === item.identifier}
                                    $isActive={activeTab === item.identifier}
                                    style={{
                                        border: activeTab === item.identifier ? "2px" : "0",
                                        borderColor:
                                            activeTab === item.identifier ? "#c8ced3 !important" : "transparent",
                                    }}
                                    className="text-primary"
                                    data-cy={
                                        stringUtils.formatUppercaseIdentifierToLowercase(item.identifier) + "-tab-link"
                                    }
                                >
                                    <NavLink
                                        style={{
                                            paddingRight:
                                                removeTab && item.removable && activeTab !== item.identifier
                                                    ? "0"
                                                    : null,
                                        }}
                                        onClick={() => setActiveTab(item.identifier)}
                                        disabled={activeTab === item.identifier}
                                    >
                                        {item.name}
                                    </NavLink>

                                    {item.tooltip && (
                                        <StyledPathwayTooltip
                                            id={item.identifier + "-tooltip"}
                                            iconSize="var(--iconSize-xs)"
                                        >
                                            {item.tooltip}
                                        </StyledPathwayTooltip>
                                    )}
                                    {removeTab && item.removable && activeTab !== item.identifier && (
                                        <DismissButton>
                                            <MdClose onClick={() => removeTab(item.identifier)} />
                                        </DismissButton>
                                    )}
                                </CustomNavItem>
                            );
                        })}

                        {addNewTab && !listUtils.isEmpty(availableTabs) && (
                            <UncontrolledDropdown setActiveFromChild>
                                <DropdownToggle
                                    tag="a"
                                    className={classnames({}, "nav-link", "bg-light", "text-dark")}
                                    caret
                                >
                                    <strong>+</strong>
                                </DropdownToggle>
                                <DropdownMenu>
                                    {availableTabs.map((item, index) => (
                                        <DropdownItem
                                            key={"language-setup-tab-dropdown-" + index}
                                            value={item.identifier}
                                            tag="a"
                                            active
                                            className={classnames({ active: false }, "bg-light", "text-dark")}
                                            onClick={() => {
                                                addNewTab(item.identifier);
                                                setActiveTab(item.identifier);
                                            }}
                                        >
                                            <strong style={{ cursor: "pointer" }}>{item.name}</strong>
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        )}
                    </Nav>
                    {tabList && (
                        <TabContent activeTab={activeTab} style={{ background: "none" }}>
                            {tabList.map((tab, index) => {
                                return (
                                    <TabPane
                                        tabId={tab.identifier}
                                        key={index}
                                        data-cy={
                                            stringUtils.formatUppercaseIdentifierToLowercase(tab.identifier) +
                                            "-tab-content"
                                        }
                                        style={{ padding: 0, background: "none" }}
                                    >
                                        {childArray[index]}
                                    </TabPane>
                                );
                            })}
                        </TabContent>
                    )}
                </>
            )}
        </>
    );
};

export default UncontrolledTabNavigation;

UncontrolledTabNavigation.propTypes = {
    defaultTab: PropTypes.string.isRequired,
    tabList: PropTypes.array.isRequired,
    dataCy: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
};

const DismissButton = styled.div`
    width: 1rem;
    display: grid;

    & > svg:hover {
        color: gray;
    }

    & > svg:active {
        color: #f0f3f5;
    }
`;

const CustomNavItem = styled(NavItem)`
    display: grid;
    grid-template-columns: max-content auto;
    align-items: start;

    font-weight: bold;
    margin-right: 2px;

    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;

    background-color: #f0f3f5 !important;
    border: 0;

    cursor: ${(props) => (props.$isActive ? undefined : "pointer")} !important;

    &:hover {
        background-color: ${(props) => (props.$isActive ? undefined : "#d1dbe1")} !important;

        & > .nav-link {
            background-color: ${(props) => (props.$isActive ? undefined : "#d1dbe1")} !important;
        }
    }

    & > .nav-link:hover {
        background-color: ${(props) => (props.$isActive ? undefined : "#d1dbe1")} !important;
    }

    & > .nav-link {
        border: none;
        background-color: #f0f3f5 !important;
        margin-bottom: 1px;
        color: ${(props) => (props.$isActive ? "var(--primary)" : "dark")} !important;
    }

    & > .nav-link:hover {
        border: none;
    }
`;

const StyledPathwayTooltip = styled(PathwayTooltip)`
    margin-right: 15px !important;
`;
